"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  name: "Revenue Audit",
  description: "A comprehensive analysis of your current OTA performance, pricing parity, and digital footprint to identify hidden revenue leaks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-sizes"
}, {
  number: "02",
  name: "Tech Integration",
  description: "Seamless implementation of modern hospitality tech stacks, including channel managers and booking engines, to streamline operations.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp-wvc-sizes"
}, {
  number: "03",
  name: "OTA Optimization",
  description: "Strategic refinement of your OTA listings, content, and imagery to improve visibility and conversion rates across all platforms.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_listing_optimization.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_listing_optimization.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_listing_optimization.png-wvc-sizes"
}, {
  number: "04",
  name: "Dynamic Pricing",
  description: "Implementation of data-driven pricing models that adjust in real-time based on market demand, competitor rates, and local events.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_strategy.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_strategy.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_strategy.webp-wvc-sizes"
}, {
  number: "05",
  name: "Direct Booking",
  description: "Targeted digital marketing campaigns and website conversion tactics designed to shift bookings from OTAs to your direct channel.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png-wvc-sizes"
}, {
  number: "06",
  name: "Performance Tracking",
  description: "Continuous monitoring of key performance indicators (KPIs) with detailed monthly reports to ensure sustained revenue growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/performance_tracking_reporting.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/performance_tracking_reporting.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/performance_tracking_reporting.webp-wvc-sizes"
}, {
  number: "07",
  name: "Strategic Scaling",
  description: "Ongoing consultancy to refine your revenue strategy, ensuring your property remains competitive and profitable in a changing market.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/strategic_scaling_consulting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/strategic_scaling_consulting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/strategic_scaling_consulting.png-wvc-sizes"
}];
function ProtocolRow({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25,
    rootMargin: "0px 0px -10% 0px"
  });
  return <div ref={ref} data-index={index} className="group relative border-t border-border/40">
      {}
      <div className="hidden md:grid grid-cols-12 gap-6 items-center py-16 lg:py-20">
        <div className="col-span-3 lg:col-span-3">
          <div className="font-default font-extralight text-[5rem] lg:text-[8rem] leading-none text-secondary transition-all duration-700 ease-out" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(40px)",
          textShadow: isVisible ? "0 0 40px hsla(43, 65%, 35%, 0.25)" : "0 0 0px hsla(43, 65%, 35%, 0)"
        }}>
            {step.number}
          </div>
        </div>

        <div className="col-span-4 lg:col-span-4">
          <h3 className="font-default font-semibold text-3xl lg:text-4xl text-foreground tracking-tight relative inline-block">
            {step.name}
            <span className="absolute -bottom-2 left-0 h-px bg-secondary transition-all duration-500 ease-out w-0 group-hover:w-full" />
          </h3>
        </div>

        <div className="col-span-4 lg:col-span-4">
          <p className="font-default text-base text-foreground/70 leading-relaxed max-w-md">
            {step.description}
          </p>
        </div>

        <div className="col-span-1 lg:col-span-1 flex justify-end">
          <div className="w-20 h-20 lg:w-24 lg:h-24 overflow-hidden rounded-lg ring-1 ring-border/40 transition-all duration-500 group-hover:ring-secondary/50">
            <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
          </div>
        </div>
      </div>

      {}
      <div className="md:hidden py-10">
        <div className="font-default font-extralight text-7xl leading-none text-secondary mb-4 transition-all duration-700" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)"
      }}>
          {step.number}
        </div>
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h3 className="font-default font-semibold text-2xl text-foreground tracking-tight mb-3">
              {step.name}
            </h3>
            <p className="font-default text-sm text-foreground/70 leading-relaxed">
              {step.description}
            </p>
          </div>
          <div className="w-20 h-20 shrink-0 overflow-hidden rounded-lg ring-1 ring-border/40">
            <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </div>;
}
export default function Implementation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3165" id="implementationprocess" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 lg:py-36 px-6 md:px-10 lg:px-16">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-lg blur-3xl opacity-20" style={{
      background: "radial-gradient(circle, var(--secondary), transparent 70%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-lg blur-3xl opacity-15" style={{
      background: "radial-gradient(circle, var(--primary), transparent 70%)"
    }} />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-20 lg:mb-28">
          <div className="lg:col-span-5 flex flex-col justify-end transition-all duration-1000 ease-out" style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? "translateY(0)" : "translateY(24px)"
        }}>
            <span className="font-default text-xs tracking-[0.3em] uppercase text-secondary mb-8">
              Our Methodology
            </span>
            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight text-foreground mb-10">
              A proven
              <br />
              <span className="italic font-light">framework</span> for
              <br />
              revenue growth.
            </h2>
            <p className="font-default text-base md:text-lg text-foreground/70 leading-relaxed max-w-lg">
              Leveraging deep industry expertise and data-driven insights, our 
              consultancy firm implements tailored revenue management strategies 
              that maximize your property's profitability and market share.
            </p>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 transition-all duration-1000 ease-out delay-200" style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? "translateY(0)" : "translateY(40px)"
        }}>
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg ring-1 ring-border/30">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_consulting_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_consulting_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_consulting_strategy.png-wvc-sizes" alt="Professional revenue management strategy session" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div aria-hidden="true" className="absolute inset-0" style={{
              background: "linear-gradient(180deg, transparent 40%, hsla(222, 47%, 7%, 0.5) 100%)"
            }} />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <span className="font-default text-[10px] tracking-[0.3em] uppercase text-foreground/80">
                  Phase I — Strategy
                </span>
                <span className="font-default text-[10px] tracking-[0.3em] uppercase text-foreground/60">
                  Global Standards
                </span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="border-b border-border/40">
          {steps.map((step, i) => <ProtocolRow key={step.number} step={step} index={i} />)}
        </div>

        {}
        <div className="mt-16 flex items-center justify-between">
          <span className="font-default text-[10px] tracking-[0.3em] uppercase text-foreground/50">
            HotelRevBaBa — Revenue Consultancy
          </span>
          <span className="font-default text-[10px] tracking-[0.3em] uppercase text-foreground/50">
            Expert Solutions
          </span>
        </div>
      </div>
    </section>;
}